// Integración de planta (coordinador; directivo en solo lectura).
//
// Tres pestañas:
//   - Conectividad: estado de señal de las líneas híbridas o de máquina
//     (GET /api/coordinador/conectividad, poll 30 s) y las ventanas sin señal
//     de los últimos 7 días (GET /api/coordinador/conectividad/ventanas).
//   - Conflictos: lo que la máquina reportó distinto de lo que una persona ya
//     registró; el coordinador decide (PATCH …/conflictos/:id/resolver).
//   - Cómo funciona: explicación corta para quien llega por primera vez.
//
// Resolver es solo del coordinador y super_admin; con "Ver como" o como
// directivo la lista se muestra sin acciones (el API respondería 403).

import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ConectividadLineaItem, ConflictoIngesta, VentanaSinSenalItem } from 'schemas';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { ConectividadBadge } from '../../../components/ui/conectividad-badge';
import { KpiCard } from '../../../components/ui/kpi-card';
import { SegmentedControl } from '../../../components/ui/segmented-control';
import { Spinner } from '../../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../../components/ui/table';
import { Tab, Tabs } from '../../../components/ui/tabs';
import { CoordinadorSidebar } from '../../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../../components/layout/RoleLayout';
import { ApiError } from '../../../lib/api/client';
import {
  useConectividadLineas,
  useConflictosIngesta,
  useResolverConflicto,
  useVentanasSinSenal,
  type EstadoConflictosFiltro,
} from '../../../lib/api/ingesta';
import { useAuth } from '../../../lib/auth';
import { tiempoRelativo } from '../../../lib/tiempoRelativo';
import { useVerComoActivo } from '../../../lib/ver-como';
import { ConflictoResolverDialog } from './ConflictoResolverDialog';
import {
  ETIQUETA_ESTADO_MAQUINA,
  ETIQUETA_FUENTE,
  etiquetaCampo,
  etiquetaRazon,
  valorLegible,
} from './etiquetas';

type Pestana = 'conectividad' | 'conflictos' | 'como-funciona';
const PESTANAS: Pestana[] = ['conectividad', 'conflictos', 'como-funciona'];

function fechaHora(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

export function IntegracionPlantaPage() {
  const { state } = useAuth();
  const verComo = useVerComoActivo();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as Pestana | null;
  const tab: Pestana = tabParam && PESTANAS.includes(tabParam) ? tabParam : 'conectividad';
  const conflictosPend = useConflictosIngesta('pendiente');

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;
  const puedeResolver =
    !verComo && (state.claims.rol === 'coordinador' || state.claims.rol === 'super_admin');
  const nPend = conflictosPend.data?.conflictos.length ?? 0;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="border-b border-border pb-3">
          <h1 className="text-xl font-medium text-fg">Integración de planta</h1>
          <p className="mt-1 text-[12px] text-fg-tertiary">
            Señal de las líneas conectadas a máquina y diferencias entre lo que reporta la máquina y
            lo que registraron las personas.
          </p>
        </header>

        <Tabs
          value={tab}
          onChange={(v) => {
            const next = new URLSearchParams(params);
            if (v === 'conectividad') next.delete('tab');
            else next.set('tab', v);
            setParams(next, { replace: true });
          }}
        >
          <Tab value="conectividad">Conectividad</Tab>
          <Tab value="conflictos">
            <span className="inline-flex items-center gap-1.5">
              Conflictos
              {nPend > 0 ? <Badge tone="warn">{nPend}</Badge> : null}
            </span>
          </Tab>
          <Tab value="como-funciona">Cómo funciona</Tab>
        </Tabs>

        {tab === 'conectividad' ? <ConectividadTab /> : null}
        {tab === 'conflictos' ? <ConflictosTab puedeResolver={puedeResolver} /> : null}
        {tab === 'como-funciona' ? <ComoFunciona /> : null}
      </div>
    </RoleLayout>
  );
}

// ─── Conectividad ───────────────────────────────────────────────────────────

function ConectividadTab() {
  const { data, isLoading, isError, error } = useConectividadLineas({ poll: true });
  const ventanas = useVentanasSinSenal({ poll: true });
  if (isLoading) return <Cargando />;
  if (isError) return <ErrorCarga error={error} />;
  return (
    <div className="flex flex-col gap-4">
      <ConectividadTabla lineas={data ?? []} />
      {(data ?? []).length > 0 && ventanas.data ? (
        <VentanasSinSenalTabla ventanas={ventanas.data} />
      ) : null}
    </div>
  );
}

function duracion(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Ventanas sin señal de los últimos 7 días: cuándo JIDOKAP dejó de oír a cada línea. */
export function VentanasSinSenalTabla({ ventanas }: { ventanas: VentanaSinSenalItem[] }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="border-b border-border px-4 py-3">
          <h2 className="text-sm font-medium text-fg">Ventanas sin señal · últimos 7 días</h2>
          <p className="mt-0.5 text-[11px] text-fg-tertiary">
            Tiempo en que JIDOKAP no recibió latidos de la línea. No es un paro ni se rellena con
            cero: lo que la planta guardó entra al reconectar.
          </p>
        </div>
        {ventanas.length === 0 ? (
          <p className="px-4 py-3 text-sm text-fg-tertiary">
            Ninguna línea quedó sin señal en los últimos 7 días.
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Línea</TH>
                <TH>Inicio</TH>
                <TH>Fin</TH>
                <TH>Duración</TH>
              </TR>
            </THead>
            <TBody>
              {ventanas.map((v) => (
                <TR key={v.id}>
                  <TD>
                    <p className="text-sm font-medium text-fg">{v.linea_nombre}</p>
                    <p className="font-mono text-[11px] text-fg-tertiary">{v.linea_codigo}</p>
                  </TD>
                  <TD className="text-fg-secondary">{fechaHora(v.inicio)}</TD>
                  <TD className="text-fg-secondary">
                    {v.fin ? fechaHora(v.fin) : <Badge tone="crit">Abierta</Badge>}
                  </TD>
                  <TD className="tabular-nums text-fg-secondary">{duracion(v.minutos)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export function ConectividadTabla({ lineas }: { lineas: ConectividadLineaItem[] }) {
  if (lineas.length === 0) {
    return (
      <Vacio
        titulo="Sin líneas conectadas"
        texto="Ninguna línea de sus plantas está configurada como híbrida o de máquina. El administrador del tenant lo configura en «Integración de planta»."
      />
    );
  }
  const cuenta = { en_linea: 0, recuperando: 0, sin_senal: 0 };
  for (const l of lineas) if (l.conectividad) cuenta[l.conectividad] += 1;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="grid grid-cols-3 gap-y-4 py-4">
          <KpiCard
            label="En línea"
            value={cuenta.en_linea}
            sub={`de ${lineas.length} conectadas`}
          />
          <KpiCard
            label="Recuperando"
            value={cuenta.recuperando}
            sub="reenviando datos atrasados"
            subTone={cuenta.recuperando > 0 ? 'warn' : 'neutral'}
            divider
          />
          <KpiCard
            label="Sin señal"
            value={cuenta.sin_senal}
            sub={cuenta.sin_senal > 0 ? 'revise la conexión en planta' : 'todas reportan'}
            subTone={cuenta.sin_senal > 0 ? 'crit' : 'ok'}
            divider
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          <Table>
            <THead>
              <TR>
                <TH>Línea</TH>
                <TH>Fuente</TH>
                <TH>Señal</TH>
                <TH>Último latido</TH>
                <TH>Estado de la máquina</TH>
                <TH>Sin señal desde</TH>
              </TR>
            </THead>
            <TBody>
              {lineas.map((l) => (
                <TR key={l.linea_id}>
                  <TD>
                    <p className="text-sm font-medium text-fg">{l.linea_nombre}</p>
                    <p className="font-mono text-[11px] text-fg-tertiary">{l.linea_codigo}</p>
                  </TD>
                  <TD className="text-fg-secondary">{ETIQUETA_FUENTE[l.fuente_captura]}</TD>
                  <TD>
                    {l.conectividad ? (
                      <ConectividadBadge
                        conectividad={l.conectividad}
                        ultimoLatidoAt={l.ultimo_latido_at}
                      />
                    ) : (
                      <span className="text-fg-tertiary">—</span>
                    )}
                  </TD>
                  <TD className="text-fg-secondary" title={fechaHora(l.ultimo_latido_at)}>
                    {l.ultimo_latido_at ? tiempoRelativo(l.ultimo_latido_at) : 'nunca'}
                  </TD>
                  <TD className="text-fg-secondary">
                    {l.estado_maquina ? ETIQUETA_ESTADO_MAQUINA[l.estado_maquina] : '—'}
                  </TD>
                  <TD title={fechaHora(l.ventana_sin_senal_abierta_desde)}>
                    {l.ventana_sin_senal_abierta_desde ? (
                      <span className="text-crit">
                        {tiempoRelativo(l.ventana_sin_senal_abierta_desde)}
                      </span>
                    ) : (
                      <span className="text-fg-tertiary">—</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Conflictos ─────────────────────────────────────────────────────────────

function ConflictosTab({ puedeResolver }: { puedeResolver: boolean }) {
  const [estado, setEstado] = useState<EstadoConflictosFiltro>('pendiente');
  const { data, isLoading, isError, error } = useConflictosIngesta(estado);
  const resolver = useResolverConflicto();
  const [abierto, setAbierto] = useState<ConflictoIngesta | null>(null);
  const [errorServidor, setErrorServidor] = useState<string | null>(null);

  const conflictos = data?.conflictos ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedControl<EstadoConflictosFiltro>
          value={estado}
          onChange={setEstado}
          options={[
            { value: 'pendiente', label: 'Pendientes' },
            { value: 'resueltos', label: 'Resueltos' },
          ]}
        />
        {!puedeResolver ? <span className="text-[11px] text-fg-tertiary">Solo lectura</span> : null}
      </div>
      {isLoading ? (
        <Cargando />
      ) : isError ? (
        <ErrorCarga error={error} />
      ) : conflictos.length === 0 ? (
        <Vacio
          titulo={estado === 'pendiente' ? 'Sin conflictos pendientes' : 'Sin conflictos resueltos'}
          texto={
            estado === 'pendiente'
              ? 'Cuando la máquina reporte algo distinto de lo que ya registró una persona, aparecerá aquí para que usted decida.'
              : 'Aquí quedan los conflictos ya decididos, con la acción tomada.'
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <ConflictosTabla
              conflictos={conflictos}
              puedeResolver={puedeResolver && estado === 'pendiente'}
              onResolver={(c) => {
                setErrorServidor(null);
                setAbierto(c);
              }}
            />
          </CardContent>
        </Card>
      )}
      {abierto ? (
        <ConflictoResolverDialog
          conflicto={abierto}
          guardando={resolver.isPending}
          serverError={errorServidor}
          onClose={() => setAbierto(null)}
          onResolver={(body) =>
            resolver.mutate(
              { id: abierto.id, body },
              {
                onSuccess: () => {
                  toast.success('Conflicto resuelto');
                  setAbierto(null);
                },
                onError: (err) =>
                  setErrorServidor(
                    err instanceof ApiError || err instanceof Error
                      ? err.message
                      : 'No se pudo resolver el conflicto',
                  ),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

export function ConflictosTabla({
  conflictos,
  puedeResolver,
  onResolver,
}: {
  conflictos: ConflictoIngesta[];
  puedeResolver: boolean;
  onResolver: (c: ConflictoIngesta) => void;
}) {
  return (
    <Table maxHeight="65vh">
      <THead sticky>
        <TR>
          <TH>Línea · hora</TH>
          <TH>Motivo</TH>
          <TH align="right">Persona</TH>
          <TH align="right">Máquina</TH>
          <TH>Estado</TH>
          {puedeResolver ? <TH /> : null}
        </TR>
      </THead>
      <TBody>
        {conflictos.map((c) => (
          <TR key={c.id}>
            <TD>
              <p className="text-sm font-medium text-fg">{c.linea_nombre}</p>
              <p className="text-[11px] tabular-nums text-fg-tertiary">
                {c.fecha ?? '—'}
                {c.hora_inicio_bloque ? ` · ${c.hora_inicio_bloque.slice(0, 5)}` : ''}
              </p>
            </TD>
            <TD>
              <p className="text-sm text-fg">{etiquetaCampo(c.campo)}</p>
              <p className="text-[11px] text-fg-tertiary">{etiquetaRazon(c.razon)}</p>
            </TD>
            <TD align="right" numeric className="text-fg">
              {valorLegible(c.valor_actual)}
            </TD>
            <TD align="right" numeric className="text-fg-secondary">
              {valorLegible(c.valor_maquina)}
            </TD>
            <TD>
              {c.estado === 'pendiente' ? (
                <Badge tone="warn">Pendiente</Badge>
              ) : c.accion === 'aplicar_maquina' ? (
                <Badge tone="info">Se aplicó la máquina</Badge>
              ) : (
                <Badge tone="neutral">Se mantuvo la persona</Badge>
              )}
            </TD>
            {puedeResolver ? (
              <TD align="right">
                <Button size="sm" variant="secondary" onClick={() => onResolver(c)}>
                  Resolver
                </Button>
              </TD>
            ) : null}
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

// ─── Explicación ────────────────────────────────────────────────────────────

function ComoFunciona() {
  const puntos: [string, string][] = [
    [
      'Qué envía la máquina',
      'Latidos, conteos acumulados, el SKU y el lote activos, y la apertura y el cierre de paros. Con eso la plataforma arma las horas de producción como si las capturara un líder.',
    ],
    [
      'Qué completa una persona',
      'La causal de los paros que la máquina no sabe clasificar y el lote y los operarios de cada hora. El líder lo hace desde la tablet; el supervisor es su respaldo.',
    ],
    [
      'Señal',
      '«En línea» si llegó un latido dentro de la ventana configurada; «Recuperando» mientras llegan datos atrasados; «Sin señal» en otro caso. Una línea sin señal en turno genera una incidencia.',
    ],
    [
      'Conflictos',
      'La máquina nunca pisa lo que ajustó una persona. Si reporta algo distinto, queda un conflicto y usted decide: mantener lo de la persona o aplicar lo de la máquina (solo para cantidad y lote).',
    ],
  ];
  return (
    <Card>
      <CardContent className="grid gap-x-8 gap-y-5 p-5 md:grid-cols-2">
        {puntos.map(([t, d], i) => (
          <div key={t} className="flex gap-3">
            <span className="font-mono text-[11px] leading-6 text-fg-tertiary">0{i + 1}</span>
            <div>
              <h3 className="text-sm font-medium text-fg">{t}</h3>
              <p className="mt-1 text-[13px] leading-relaxed text-fg-secondary">{d}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Estados ────────────────────────────────────────────────────────────────

function Cargando() {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-fg-secondary">
      <Spinner className="size-4" /> Cargando…
    </div>
  );
}

function ErrorCarga({ error }: { error: unknown }) {
  return (
    <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-sm text-crit">
      {error instanceof Error ? error.message : 'No pudimos cargar la información'}
    </p>
  );
}

function Vacio({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <div className="rounded border border-dashed border-border px-4 py-10 text-center">
      <p className="text-sm font-medium text-fg">{titulo}</p>
      <p className="mx-auto mt-1 max-w-md text-[12px] text-fg-tertiary">{texto}</p>
    </div>
  );
}
